import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ApplicationFilterState } from "../components/ApplicationsFilter/ApplicationsFilter";
import { ErrorToast, SuccessToast } from "../components/toastMessage";
import { _axios } from "../utils/axios";

export type TApplication = {
  id: number;
  userId: number;
  status: string;
  createdAt: string;
  updatedAt: string;
  submittedAt?: string;
  applicationId: string;
  project_name?: string;
  plot_owner_s_name?: string;
  daysLeftForAction?: number | null;
  isActionAllowed?: boolean; 
  level2QueueAt?: number | null;
  level2SubmissionDate?: number | null;
};

export const level2ApplicationsKeyStore = {
  savedDemandNoteData: (...args: (number | string | boolean | undefined)[]) => [
    "level-2-demand-note-data",
    ...args,
  ],
  lastReview: (id?: number | string) => ["level-2-application-last-review", id],
  allReviews: (id?: number | string) => ["level-2-application-all-reviews", id],
  id: (arg: number | string) => ["level-2-application", arg],
  decisionMaps: (arg?: number | string) => ["level-2-decision-maps", arg],
  counts: (...args: (number | string | Record<string, unknown>)[]) => [
    "level-2-applications-counts",
    ...args,
  ],
  list: (...args: (number | string | Record<string, unknown>)[]) => [
    "level-2-applications",
    ...args,
  ],
  stats: (...args: (number | string | Record<string, unknown>)[]) => [
    "level-2-applications-stats",
    ...args,
  ],
};

export function useApplicationStatsForLevel2({
  filters,
}: {
  filters?: ApplicationFilterState;
}) {
  return useQuery({
    queryKey: level2ApplicationsKeyStore.stats(filters || {}),
    queryFn: () =>
      _axios.post<{
        success: true;
        hasMore?: boolean;
        data: { status: string; count: number }[];
      }>("/level-2-application-approval/stats", { filters }),
    staleTime: 2 * 60 * 1000,
    refetchInterval: 2 * 60 * 1000,
    select: (d) => d.data,
  });
}

export function useApplicationCountForLevel2({ search, filters }: {
  search?: string;
  filters?: ApplicationFilterState;
}) {
  return useQuery({
    queryKey: level2ApplicationsKeyStore.counts(search || "", filters || {}),
    queryFn: () =>
      _axios.post<{
        success: true;
        hasMore?: boolean;
        data: number;
      }>("/level-2-application-approval/count", { search, filters }),
    staleTime: 2 * 60 * 1000,
    refetchInterval: 2 * 60 * 1000,
    select: (d) => d.data,
  });
}

export function useApplicationsForLevel2({
  page,
  limit,
  search,
  sort,
  sortVal,
  filters,
}: {
  page: number;
  limit: number;
  search?: string;
  sort: string;
  sortVal: number;
  filters?: ApplicationFilterState;
}) {
  return useQuery({
    queryKey: level2ApplicationsKeyStore.list(
      page,
      limit,
      sort,
      sortVal,
      search || "",
      filters || {}
    ),
    queryFn: () =>
      _axios.post<{
        success: true;
        hasMore?: boolean;
        data: TApplication[];
      }>("/level-2-application-approval/list", {
        sort,
        sortVal,
        search,
        page,
        limit,
        filters: filters || {},
      }),
    staleTime: 2 * 60 * 1000,
    refetchInterval: 2 * 60 * 1000,
    select: (d) => d.data,
  });
}

export function useApplicationForLevel2({ id }: { id: number | string }) {
  return useQuery({
    queryKey: level2ApplicationsKeyStore.id(id),
    queryFn: () =>
      _axios.get<{
        success: true;
        data: TApplication & Record<string, unknown>;
      }>(`/level-2-application-approval/${id}`),
    select: (d) => d?.data?.data,
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
  });
}

export function useLevel2DecisionMaps({ id }: { id: undefined | number }) {
  return useQuery({
    enabled: !!id,
    queryKey: level2ApplicationsKeyStore.decisionMaps(id),
    queryFn: () =>
      _axios.get<{
        success: true;
        data: {
          nocDecisions: Record<string, boolean>;
          documentDecisions: Record<string, boolean>;
        };
      }>(`/level-2-application-approval/decision-map/${id}`),
    select: (res) => res?.data?.data,
  });
}

export function useLevel2UpdateDocumentDecision() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: {
      docId: number;
      decision: 0 | 1;
      applicationId: number;
    }) =>
      _axios.post("/level-2-application-approval/update-doc-decision", payload),
    onSettled: (_, __, { applicationId }) =>
      qc.invalidateQueries({
        queryKey: level2ApplicationsKeyStore.decisionMaps(applicationId),
        exact: true,
      }),
    onError: () => {
      ErrorToast("Something went wrong!");
    },
    onSuccess: () => {
      SuccessToast("Updated!");
    },
  });
}

export function useLevel2UpdateNocDecision() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: {
      nocId: number;
      decision: 0 | 1;
      applicationId: number;
    }) =>
      _axios.post("/level-2-application-approval/update-noc-decision", payload),
    onSettled: (_, __, { applicationId }) =>
      qc.invalidateQueries({
        queryKey: level2ApplicationsKeyStore.decisionMaps(applicationId),
        exact: true,
      }),
    onError: () => {
      ErrorToast("Something went wrong!");
    },
    onSuccess: () => {
      SuccessToast("Updated!");
    },
  });
}

export function useLevel2LastReview({ id }: { id?: number }) {
  return useQuery({
    queryKey: level2ApplicationsKeyStore.lastReview(id),
    enabled: !!id,
    queryFn: () =>
      _axios.get<{
        success: true;
        data: {
          fieldsToChange?: number[];
          review?: { comment_text?: string; id?: number };
        };
      }>(`/level-2-application-approval/get-current-review/${id}`),
    select: (result) => result.data?.data,
  });
}

export type TReviewWithReviewer = {
  id: number;
  date: string;
  comment_text: string;
  level: string;
  reviewer?: { name: string; role: string };
};

export function useLevel2AllReviews({ id }: { id?: number }) {
  return useQuery({
    queryKey: level2ApplicationsKeyStore.allReviews(id),
    enabled: !!id,
    queryFn: () =>
      _axios.get<{
        success: true;
        data: TReviewWithReviewer[];
      }>(`/level-2-application-approval/get-all-reviews/${id}`),
    select: (result) => result.data?.data,
  });
}

export function useSaveLevel2Review() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: {
      applicationId: number;
      comment: string;
      fieldsToChange: number[];
    }) => _axios.post("/level-2-application-approval/save-review", data),
    onSuccess: () => SuccessToast("Saved!"),
    onError: () => ErrorToast("Something went wrong while saving review!"),
    onSettled: (_, __, { applicationId }) => {
      qc.invalidateQueries({
        queryKey: level2ApplicationsKeyStore.lastReview(applicationId),
      });
      qc.invalidateQueries({
        queryKey: level2ApplicationsKeyStore.allReviews(applicationId),
      });
    },
  });
}

export function useLevel2RejectApplication() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: {
      applicationId: number;
      user_id: number;
      application_id: string;
    }) => _axios.post("/level-2-application-approval/reject-application", data),
    onSettled: (_, __, { applicationId }) => {
      qc.invalidateQueries({ queryKey: level2ApplicationsKeyStore.list() });
      qc.invalidateQueries({ queryKey: level2ApplicationsKeyStore.stats() });
    },
    onSuccess: () => SuccessToast("Application rejected successfully!"),
    onError: () =>
      ErrorToast("Something went wrong while rejecting application!"),
  });
}

export function useLevel2ReturnApplication() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: {
      applicationId: number;
      user_id: number;
      application_id: string;
    }) => _axios.post("/level-2-application-approval/return-application", data),
    onSettled: (_, __, { applicationId }) => {
      qc.invalidateQueries({ queryKey: level2ApplicationsKeyStore.list() });
      qc.invalidateQueries({ queryKey: level2ApplicationsKeyStore.stats() });
    },
    onSuccess: () => SuccessToast("Application returned successfully!"),
    onError: () =>
      ErrorToast("Something went wrong while returning application!"),
  });
}

export type TSavedDemandNoteData = {
  id?: number;
  municipality?: string;
  tehsil?: string;
  district?: string;
  dueDate?: string;
  applicantName?: string;
  layoutName?: string;
  baseAmount?: number;
  interestAccumulated?: number;
  daysPending?: number;
  area?: number;
  unit_area?: string;
  plotUsageType?: string;
  mauja?: string;
  khasaraNo?: string;
  main_khasara_no?: string;
  manual_khasara_no?: string;
  constructionArea?: number;
  particular1Rate?: number;
  particular1Total?: number;
  particular2Rate?: number;
  particular2Total?: number;
  supervisionTotal?: number;
  //supervisionRate?: number;
  totalAmount?: number;
  plotNo?: string;
  scrutinyCharges?: number;
  conditions?: string;
  buildingDevelopmentRRLandRate?: number;
  buildingDevelopmentRRLandTotal?: number;
  labourCessRRConstructionRate?: number;
  labourCessRRConstructionTotal?: number;
  chargesForConstructioInSideMargineRRLandRate?: number;
  chargesForConstructioInSideMargineRRLandTotal?: number;
  areaExcessOfBasicFSI?: number;
  rainWaterHarwesting?: number;
  securityDeposite?: number;
  supervisionCharges?: number;
  supervision_charges?: number;
  compoundingCharges?: number;
  swmCharges?: number;
  administrativeCharges?: number;
  surveyMeasurementCharges?: number;
  infrastructureFee?: number;
  areaOfPlot?: number;
  rrLandRate?: number;
  regularizationPercent?: number;
  developmentPercent?: number;
  chargesForExcessBuiltUpArea?: number;
  percentRRLandRateForExcessBuiltUpArea?: number;
  supervisionChargesForOpenPlot?: number;
  supervisionChargesForConstruction?: number;
  areaOfDemolish?: number;
  areaOfExcessBuiltUp?: number;
  excessBuiltUpArea?: number;
};

export function useSavedDemandNoteDataForLevel2({
  applicationId,
  reviewId,
  isThereAConst,
}: {
  applicationId?: number;
  reviewId?: number;
  isThereAConst?: boolean;
}) {
  return useQuery({
    queryKey: level2ApplicationsKeyStore.savedDemandNoteData(
      applicationId,
      reviewId,
      isThereAConst
    ),
    select: (d) => d.data?.data,
    queryFn: () =>
      _axios.get<{ success: true; data?: TSavedDemandNoteData }>(
        "/level-2-application-approval/get-saved-demand-note-data",
        {
          params: { applicationId, reviewId, isThereAConst },
        }
      ),
  });
}

export function useSaveDemandNoteDataForLevel2() {
  return useMutation({
    mutationFn: (data: {
      applicationId: number;
      reviewId: number;
      data: Record<string, unknown>;
      isThereAConst: boolean;
    }) =>
      _axios.post(
        "level-2-application-approval/save-demand-note-data",
        data,
        {
          responseType: "blob", // ✅ tells Axios + TS this is binary
        }
      ),
    onError: () =>
      ErrorToast("Something went wrong while generating sample demand note!"),
  });
}

export function useForwardApplicationForLevel2() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: {
      applicationId: number;
      reviewId: number;
      isThereAConst: boolean;
    }) =>
      _axios.post<{ success: true }>(
        "level-2-application-approval/approve-application",
        data
      ),
    onSuccess: (_, { applicationId }) => {
      SuccessToast("Application Forwarded!");
      qc.invalidateQueries({
        queryKey: level2ApplicationsKeyStore.id(applicationId),
      });
      qc.invalidateQueries({ queryKey: level2ApplicationsKeyStore.list() });
      qc.invalidateQueries({ queryKey: level2ApplicationsKeyStore.stats() });
    },
    onError: () =>
      ErrorToast("Something went wrong while forwarding application!"),
  });
}
